import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  content: string;
  className?: string;
  children?: React.ReactNode;
}

const TOOLTIP_MAX_WIDTH = 280;
const VIEWPORT_MARGIN = 8;
const GAP = 6;

export const Tooltip: React.FC<TooltipProps> = ({ content, className, children }) => {
  const tooltipId = useId();
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLElement | null>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);

  const show = () => setVisible(true);
  const hide = () => setVisible(false);

  useLayoutEffect(() => {
    if (!visible || !buttonRef.current) return;
    const updatePosition = () => {
      const button = buttonRef.current;
      const bubble = bubbleRef.current;
      if (!button || !bubble) return;
      const iconRect = button.getBoundingClientRect();
      const bubbleWidth = bubble.offsetWidth;
      const iconCenter = iconRect.left + iconRect.width / 2;
      let left = iconCenter - bubbleWidth / 2;
      left = Math.max(VIEWPORT_MARGIN, left);
      left = Math.min(left, window.innerWidth - bubbleWidth - VIEWPORT_MARGIN);
      const top = iconRect.bottom + GAP;
      setCoords({ top, left });
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [visible]);

  const triggerProps = {
    ref: (el: HTMLElement | null) => { buttonRef.current = el; },
    'aria-describedby': tooltipId,
    onMouseEnter: show,
    onMouseLeave: hide,
    onFocus: show,
    onBlur: hide,
  } as any;

  return (
    <span className={`tooltip-wrapper ${className ?? ''}`}>
      {children ? (
        <span {...triggerProps} tabIndex={0}>
          {children}
        </span>
      ) : (
        <button
          ref={buttonRef as any}
          type="button"
          className="tooltip-icon"
          aria-describedby={tooltipId}
          onMouseEnter={show}
          onMouseLeave={hide}
          onFocus={show}
          onBlur={hide}
        >
          ?
        </button>
      )}
      {visible &&
        createPortal(
          <span
            ref={bubbleRef}
            id={tooltipId}
            role="tooltip"
            className="tooltip-bubble visible"
            style={{
              top: coords ? `${coords.top}px` : 0,
              left: coords ? `${coords.left}px` : 0,
              maxWidth: TOOLTIP_MAX_WIDTH,
              visibility: coords ? 'visible' : 'hidden',
            }}
          >
            {content}
          </span>,
          document.body
        )}
    </span>
  );
};
